import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import React, { useRef, useState } from "react";
import { flushSync } from "react-dom";
import { useQuery } from "react-query";
import { useReactToPrint } from "react-to-print";
import useAuth from "src/hooks/useAuth";
import { getAttendanceForCompany, getClientsMini, getAssignedQrCodes } from "src/pages/api";
import PrintableClientQrCodes from "../client-qr-code/PrintableClientQrCodes";

export default function PrintQrDialog({ open, onClose }) {
  const { user } = useAuth();
  const isCompanyUser = user?.role === "COMPANY";

  const [selectedCompany, setSelectedCompany] = useState(null);
  const [companyInputValue, setCompanyInputValue] = useState("");
  const [companySearch, setCompanySearch] = useState("");
  const companySearchTimer = useRef(null);

  const [selectedClient, setSelectedClient] = useState(null);
  const [clientInputValue, setClientInputValue] = useState("");
  const [clientSearch, setClientSearch] = useState("");
  const clientSearchTimer = useRef(null);

  const [selected, setSelected] = useState([]);
  const [printing, setPrinting] = useState(false);
  const printRef = useRef(null);
  const [printPayload, setPrintPayload] = useState({ clientName: "", qrCodes: [] });

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    documentTitle: printPayload.clientName ? `${printPayload.clientName}-qr-codes` : "qr-codes",
    onAfterPrint: () => setPrinting(false),
  });

  const { data: companies = [], isFetching: companiesLoading } = useQuery({
    queryKey: ["companies-search-print", companySearch],
    queryFn: ({ signal }) => getAttendanceForCompany({ signal, companySearch }),
    enabled: open && !isCompanyUser,
    keepPreviousData: true,
  });

  const { data: clientOptions = [], isFetching: clientsLoading } = useQuery({
    queryKey: ["clients-mini-print", clientSearch, selectedCompany?.id],
    queryFn: ({ signal }) =>
      getClientsMini({ signal, search: clientSearch, limit: 50, companyId: selectedCompany?.id || undefined }),
    enabled: open && (isCompanyUser || !!selectedCompany?.id),
    keepPreviousData: true,
  });

  const { data: qrList = [], isFetching: qrLoading } = useQuery({
    queryKey: ["assigned-qr-codes", selectedClient?.id],
    queryFn: ({ signal }) => getAssignedQrCodes({ clientId: selectedClient.id, signal }),
    enabled: open && !!selectedClient?.id,
    onSuccess: (data) => setSelected(data.map((_, i) => i)),
  });

  const handleCompanyInputChange = (_, value) => {
    setCompanyInputValue(value);
    clearTimeout(companySearchTimer.current);
    companySearchTimer.current = setTimeout(() => setCompanySearch(value), 400);
  };

  const handleClientInputChange = (_, value) => {
    setClientInputValue(value);
    clearTimeout(clientSearchTimer.current);
    clientSearchTimer.current = setTimeout(() => setClientSearch(value), 400);
  };

  const handleClose = () => {
    setSelectedCompany(null);
    setCompanyInputValue("");
    setCompanySearch("");
    setSelectedClient(null);
    setClientInputValue("");
    setClientSearch("");
    setSelected([]);
    onClose();
  };

  const allSelected = qrList.length > 0 && selected.length === qrList.length;
  const someSelected = selected.length > 0 && !allSelected;

  const toggleAll = () => setSelected(allSelected ? [] : qrList.map((_, i) => i));
  const toggleOne = (i) =>
    setSelected((prev) => (prev.includes(i) ? prev.filter((x) => x !== i) : [...prev, i]));

  const handleDownload = () => {
    if (!selected.length) return;
    setPrinting(true);
    const qrCodes = selected.map((i) => ({
      code: qrList[i].qrCode,
      qrCodeName: qrList[i].checklistName,
      companyName: qrList[i].companyName,
      companyLogo: qrList[i].companyLogo,
      clientName: qrList[i].clientName,
    }));
    flushSync(() => {
      setPrintPayload({ clientName: selectedClient?.clientName || "", qrCodes });
    });
    handlePrint();
  };

  return (
    <>
      {/* Hidden printable sheet */}
      <Box sx={{ position: "fixed", left: -99999, top: -99999, zIndex: -1 }}>
        <PrintableClientQrCodes
          ref={printRef}
          clientName={printPayload.clientName}
          qrCodes={printPayload.qrCodes}
        />
      </Box>

      <Dialog open={open}  maxWidth="xs" fullWidth>
        <DialogTitle
          sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pb: 1 }}
        >
          <Typography fontWeight={700} fontSize="1rem">Print QR Code</Typography>
          <IconButton size="small" onClick={handleClose}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <Divider />

        <DialogContent sx={{ pt: 2, pb: 3 }}>
          <Stack spacing={2}>
            {/* Company autocomplete - hidden for COMPANY role */}
            {!isCompanyUser && (
              <Autocomplete
                options={companies}
                loading={companiesLoading}
                value={selectedCompany}
                onChange={(_, val) => {
                  setSelectedCompany(val);
                  setSelectedClient(null);
                  setClientInputValue("");
                  setSelected([]);
                }}
                inputValue={companyInputValue}
                onInputChange={handleCompanyInputChange}
                getOptionLabel={(opt) => opt?.companyName || opt?.name || ""}
                isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
                noOptionsText="No companies found"
                filterOptions={(x) => x}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    size="small"
                    label="Company"
                    placeholder="Search company..."
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <>
                          {companiesLoading ? <CircularProgress color="inherit" size={16} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      ),
                    }}
                  />
                )}
              />
            )}

            {/* Client dropdown */}
            <Autocomplete
              options={clientOptions}
              loading={clientsLoading}
              value={selectedClient}
              disabled={!isCompanyUser && !selectedCompany?.id}
              onChange={(_, val) => {
                setSelectedClient(val);
                setSelected([]);
              }}
              inputValue={clientInputValue}
              onInputChange={handleClientInputChange}
              getOptionLabel={(opt) => opt?.clientName || ""}
              isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
              noOptionsText="No clients found"
              filterOptions={(x) => x}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  label="Client"
                  placeholder="Search client..."
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {clientsLoading ? <CircularProgress color="inherit" size={16} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />

            {/* QR loading */}
            {selectedClient && qrLoading && (
              <Stack alignItems="center" py={2} spacing={1}>
                <CircularProgress size={24} sx={{ color: "#2563EB" }} />
                <Typography variant="body2" color="text.secondary">Loading QR codes…</Typography>
              </Stack>
            )}

            {/* No QR codes */}
            {selectedClient && !qrLoading && qrList.length === 0 && (
              <Typography variant="body2" color="text.secondary" textAlign="center" py={1}>
                No QR codes assigned to this client.
              </Typography>
            )}

            {/* Checklist QR list */}
            {selectedClient && !qrLoading && qrList.length > 0 && (
              <>
                <Box sx={{ border: "1px solid #E5E7EB", borderRadius: "12px", overflow: "hidden" }}>
                  {/* Select All */}
                  <Box sx={{ px: 2, py: 1, backgroundColor: "#F9FAFB", borderBottom: "1px solid #E5E7EB" }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          size="small"
                          checked={allSelected}
                          indeterminate={someSelected}
                          onChange={toggleAll}
                          sx={{ color: "#2563EB", "&.Mui-checked, &.MuiCheckbox-indeterminate": { color: "#2563EB" } }}
                        />
                      }
                      label={
                        <Typography fontWeight={700} fontSize="0.83rem" color="#374151">
                          Select All ({qrList.length})
                        </Typography>
                      }
                      sx={{ m: 0 }}
                    />
                  </Box>

                  <Box sx={{ maxHeight: 220, overflowY: "auto" }}>
                    {qrList.map((item, i) => (
                      <React.Fragment key={item.assignmentId ?? i}>
                        <Box
                          sx={{
                            px: 2,
                            py: 0.9,
                            display: "flex",
                            alignItems: "center",
                            "&:hover": { backgroundColor: "#F5F8FF" },
                            transition: "background 0.15s",
                          }}
                        >
                          <FormControlLabel
                            control={
                              <Checkbox
                                size="small"
                                checked={selected.includes(i)}
                                onChange={() => toggleOne(i)}
                                sx={{ color: "#2563EB", "&.Mui-checked": { color: "#2563EB" } }}
                              />
                            }
                            label={
                              <Box>
                                <Typography fontSize="0.85rem" color="#111827" fontWeight={500}>
                                  {item.checklistName}
                                </Typography>
                                <Typography fontSize="0.75rem" color="#6B7280">
                                  {item.qrCode}
                                </Typography>
                              </Box>
                            }
                            sx={{ m: 0, flex: 1 }}
                          />
                        </Box>
                        {i < qrList.length - 1 && <Divider sx={{ mx: 2 }} />}
                      </React.Fragment>
                    ))}
                  </Box>
                </Box>

                {/* Print button */}
                <Button
                  fullWidth
                  variant="contained"
                  disabled={selected.length === 0 || printing}
                  onClick={handleDownload}
                  sx={{
                    borderRadius: "12px",
                    textTransform: "none",
                    fontWeight: 700,
                    fontSize: "0.95rem",
                    py: 1.3,
                    backgroundColor: "#2563EB",
                    "&:hover": { backgroundColor: "#1D4ED8" },
                    "&.Mui-disabled": { backgroundColor: "#E5E7EB", color: "#9CA3AF" },
                  }}
                >
                  {printing ? (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <CircularProgress size={16} sx={{ color: "#fff" }} />
                      <span>Preparing…</span>
                    </Stack>
                  ) : (
                    `Print / Download QR${selected.length > 0 ? ` (${selected.length})` : ""}`
                  )}
                </Button>
              </>
            )}
          </Stack>
        </DialogContent>
      </Dialog>
    </>
  );
}
